import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useQueryClient } from "@tanstack/react-query";
import { useQueryState, useQueryStates } from "nuqs";
import { useEffect, useMemo, useState } from "react";
import { useHistory, useParams } from "react-router-dom";

import {
  Loader,
  NotificationType,
  Tab,
  TabHeader,
  TabHeaders,
  Tabs,
  TextTooltip,
} from "@transfr-inc/dashboard-components";
import { Product } from "@transfr-inc/dashboard-components/constants";
import { PageLayout } from "@transfr-inc/dashboard-components/layouts";
import {
  useCourseActivityStatusByClassroom,
  useLiveClassroomCount,
} from "@transfr-inc/dashboard-sockets";

import { RecordStatus } from "@components/custom/dropdowns";
import { useContainer } from "@contexts/container-context";
import { useNotificationContext } from "@contexts/notification-context";
import {
  useArchiveClassroomEnabled,
  useCourseBuilderEnabled,
} from "@hooks/queries/account-selectors";
import {
  trackMissionControlTabClick,
  trackPreviewClassroomButtonClicked,
  trackView,
} from "@lib/event-trackers";
import { useApiRequest } from "@lib/hooks";
import { hasAnyProducts } from "@lib/product.utils";
import { createMyWorkLink } from "@lib/utils/navigation.utils";
import {
  classroomDetailParams,
  productParser,
} from "@lib/utils/search-params-parsers";
import { ViewClassroom } from "@models/tracking/events";
import {
  useAddClassroomInstructors,
  useClassroomDetails,
  useCurrentUser,
  useFeatures,
  useMissionControlEnabled,
  usePreviewClassroomModeEnabled,
  useRemoveClassroomInstructors,
  useUpdateClassroom,
} from "@queries";
import { RosterModal } from "@views/users/roster/roster-modal";

import { ClassroomHeader } from "./components/classroom.header";
import { ClassroomToolbar } from "./components/classroom.toolbar";
import { getUserLabelsByProduct } from "./utils/labels";
import {
  ClassroomCourseBuilderTab,
  ClassroomGradebookTab,
  ClassroomMenuBuilderTab,
  ClassroomMissionControlTab,
  ClassroomProgressTab,
  ClassroomQRTab,
  ClassroomSimPassTab,
  ClassroomUsersTab,
} from "./views";

import "./index.scss";

const ClassroomDetailsTabs = {
  Progress: "progress",
  Gradebook: "gradebook",
  CourseBuilder: "courseBuilder",
  MenuBuilder: "menuBuilder",
  QR: "qr",
  SimPass: "simPass",
  MissionControl: "missionControl",
};

export default ({ responsiveStyles }) => {
  const [openRosterModal, setOpenRosterModal] = useState();
  const [statusFilter, setStatusFilter] = useState(RecordStatus.Active);

  const { classroomId } = useParams();
  const { data: classroom, isFetching: isFetchingClassroom } =
    useClassroomDetails(classroomId);

  const [
    { from: breadcrumbLabel, tab: selectedTab, user: selectedUser },
    setClassroomParams,
  ] = useQueryStates(classroomDetailParams);

  const [selectedProductId] = useQueryState("product", productParser());

  const activeProduct = selectedProductId || classroom?.products?.[0];
  const { userTabTitle } = getUserLabelsByProduct(activeProduct) ?? {
    userTabTitle: "Users",
  };

  const { addNotification } = useNotificationContext();
  const queryClient = useQueryClient();
  const { classroomService, courseService } = useContainer();
  const features = useFeatures();
  const currentUser = useCurrentUser();
  const missionControlEnabled = useMissionControlEnabled();
  const isPreviewClassroomEnabled = usePreviewClassroomModeEnabled();
  const isCourseBuilderEnabled = useCourseBuilderEnabled();
  const archiveClassroomEnabled = useArchiveClassroomEnabled(
    classroom?.clientCode
  );

  const isArchiveClassroomEnabled =
    archiveClassroomEnabled && !classroom?.isArchived && !isFetchingClassroom;

  const isUnarchiveClassroomEnabled =
    archiveClassroomEnabled && classroom?.isArchived && !isFetchingClassroom;

  const { editClassroomEnabled, deleteClassroomEnabled } = features || {};
  const isClassroomEditable = editClassroomEnabled && !classroom?.isArchived;

  const history = useHistory();
  const { token } = currentUser || {};
  const socketUrl = process.env.SOCKET_BRIDGE_URL;
  const classroomOrgCode = classroom?.clientCode;

  const previewClassroomEnabled = useMemo(
    () =>
      isPreviewClassroomEnabled &&
      hasAnyProducts(classroom?.products, [Product.TS, Product.PROG]) &&
      !classroom?.isProfessionalLearning,
    [isPreviewClassroomEnabled, classroom]
  );

  const tabsEnabled = useMemo(() => {
    return {
      missionControl:
        missionControlEnabled &&
        hasAnyProducts(activeProduct, [Product.CE, Product.TRK]),
      gradebook: hasAnyProducts(activeProduct, [Product.TS, Product.PROG]),
      courseBuilder:
        isCourseBuilderEnabled && hasAnyProducts(activeProduct, [Product.PROG]),
      menuBuilder:
        classroom?.classroomMenuBuilder &&
        hasAnyProducts(activeProduct, [Product.TS, Product.CE]),
    };
  }, [missionControlEnabled, isCourseBuilderEnabled, classroom, activeProduct]);

  const {
    loading: loadingUsers,
    response,
    sendRequest: getUsers,
  } = useApiRequest(
    () => classroomService.getClassroomTrainees(classroomId, statusFilter),
    false
  );
  const users = response ?? [];
  const initialUsersLoaded = response !== undefined;

  useEffect(() => {
    getUsers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusFilter, classroom]);

  const { mutateAsync: updateClassroom } = useUpdateClassroom(classroomId);
  const { mutateAsync: removeInstructors } =
    useRemoveClassroomInstructors(classroomId);
  const { mutateAsync: addInstructors } =
    useAddClassroomInstructors(classroomId);

  const {
    response: courseInfo,
    sendRequest: getCourseInfo,
    loading: courseDataLoading,
  } = useApiRequest(
    () =>
      courseService.getCourseDetails(classroom?.courseId, {
        include_activity_metadata: true,
      }),
    false
  );

  useEffect(() => {
    if (classroom && classroom?.courseId) {
      getCourseInfo();
    }
  }, [classroom]);

  const activityStatus = useCourseActivityStatusByClassroom(
    socketUrl,
    token,
    classroom?.courseId,
    classroom?.classroomId
  );

  const onEditClassroom = async (data) => {
    const { instructors, ...updatedClassroom } = data;
    const requests = [updateClassroom(updatedClassroom)];

    if (instructors.toRemove.length > 0) {
      requests.push(
        removeInstructors(
          instructors.toRemove.map((instructor) => instructor.userId)
        )
      );
    }
    if (instructors.toAdd.length > 0) {
      requests.push(
        addInstructors(instructors.toAdd.map((instructor) => instructor.userId))
      );
    }

    const results = await Promise.allSettled(requests);
    const errors = results.filter((result) => result.status === "rejected");

    if (errors.length > 0) {
      throw new Error(errors.map((error) => error.reason).join(", "));
    } else {
      addNotification({
        type: NotificationType.success,
        icon: ["fa-solid", "circle-check"],
        message: (
          <>
            The details for <strong>{updatedClassroom.name}</strong> have been
            updated.
          </>
        ),
      });
      queryClient.invalidateQueries({
        queryKey: ["classroomDetails", classroomId],
      });
      setTabState(ClassroomDetailsTabs.Progress);
    }
  };

  const onDeleteClassroom = () => {
    return new Promise((resolve) => {
      setTimeout(() => {
        resolve();
      }, 1500);
    });
  };

  const onPreviewClassroom = () => {
    trackPreviewClassroomButtonClicked(
      classroomId,
      String(classroom?.courseId),
      classroom?.classroomMenuId ? "custom" : classroom?.organizationMenuId,
      String(activeProduct)
    );
    history.push(
      createMyWorkLink({
        search: {
          preview: classroomId,
        },
        state: {
          courseId: classroom?.courseId,
          courseInfo,
        },
      })
    );
  };

  const onArchiveClassroomSuccess = ({ archived }) => {
    if (archived.length > 0) {
      addNotification({
        type: NotificationType.success,
        icon: ["fa-solid", "circle-check"],
        message: `${classroom.name} archived successfully.`,
      });
    }
  };

  const onUnarchiveClassroomSuccess = () => {
    addNotification({
      type: NotificationType.success,
      icon: ["fa-solid", "circle-check"],
      message: `${classroom.name} unarchived successfully.`,
    });
  };

  const onUnarchiveClassroomError = (error) => {
    addNotification({
      type: NotificationType.error,
      message: "Something went wrong. Please try again.",
    });
  };

  const onUsersUpdate = () => {
    getUsers();
  };

  const setTabState = (tabName) => setClassroomParams({ tab: tabName });

  const TRACKED_TABS = {
    [ClassroomDetailsTabs.Progress]: "progress",
    [ClassroomDetailsTabs.Gradebook]: "gradebook",
    [ClassroomDetailsTabs.CourseBuilder]: "course_builder",
    [ClassroomDetailsTabs.MenuBuilder]: "menu_builder",
    [userTabTitle]: "learners",
  };

  useEffect(() => {
    if (!classroom) return;

    const activeTab =
      TRACKED_TABS[selectedTab ?? ClassroomDetailsTabs.Progress];

    if (!activeTab) return;

    trackView(
      ViewClassroom,
      "Classroom Details",
      activeTab,
      String(activeProduct),
      classroom?.classroomId
    );
  }, [selectedTab, userTabTitle, classroom, activeProduct]);

  const pageHeader = classroom && (
    <ClassroomHeader
      classroom={classroom}
      breadcrumbLabel={breadcrumbLabel}
      courseInfo={courseInfo}
    >
      <ClassroomToolbar
        classroom={classroom}
        hasEdit={isClassroomEditable}
        hasDelete={deleteClassroomEnabled}
        hasPreview={previewClassroomEnabled}
        onEditClassroom={onEditClassroom}
        onDeleteClassroom={onDeleteClassroom}
        onPreviewClassroom={onPreviewClassroom}
        hasArchive={isArchiveClassroomEnabled}
        onArchiveClassroomSuccess={onArchiveClassroomSuccess}
        hasUnarchive={isUnarchiveClassroomEnabled}
        onUnarchiveClassroomSuccess={onUnarchiveClassroomSuccess}
        onUnarchiveClassroomError={onUnarchiveClassroomError}
        loading={isFetchingClassroom}
      />
    </ClassroomHeader>
  );

  const liveStudentCount = missionControlEnabled
    ? useLiveClassroomCount(socketUrl, token, classroomOrgCode, classroomId)
    : undefined;

  const handleMissionControlTabClick = () => {
    trackMissionControlTabClick(classroomId);
    setTabState(ClassroomDetailsTabs.MissionControl);
  };

  const onTabChange = async (name) => {
    // Course builder tab mutates the course data locally
    // so we need to refetch the data when selecting this tab
    // to get latest information
    if (name == ClassroomDetailsTabs.CourseBuilder) {
      await getCourseInfo();
    }
  };

  if (loadingUsers && !initialUsersLoaded) {
    return <Loader />;
  }

  return (
    classroom && (
      <PageLayout className={"trainee-detail-page"} header={pageHeader}>
        <div className="classroom-details-page">
          <div className="classroom-tabs">
            <Tabs
              staticHeaders
              selectedTabName={selectedTab ?? ClassroomDetailsTabs.Progress}
            >
              <TabHeaders
                className="classroom-tabs-headers"
                onSelectedTabChange={onTabChange}
              >
                <TabHeader
                  name={ClassroomDetailsTabs.Progress}
                  onTabClick={setTabState}
                >
                  Progress
                </TabHeader>
                <TabHeader name={userTabTitle} onTabClick={setTabState}>
                  {userTabTitle}
                  <span className="badge">
                    {loadingUsers ? "..." : users.length}
                  </span>
                </TabHeader>
                {!classroom?.isProfessionalLearning && (
                  <TextTooltip
                    text={
                      classroom?.isArchived &&
                      "Quick Sign Up is unavailable because this classroom is archived"
                    }
                  >
                    <TabHeader
                      name={ClassroomDetailsTabs.QR}
                      onTabClick={setTabState}
                      disabled={classroom?.isArchived}
                    >
                      {"Quick Sign Up (QR)"}
                    </TabHeader>
                  </TextTooltip>
                )}
                {tabsEnabled?.courseBuilder && (
                  <TabHeader
                    name={ClassroomDetailsTabs.CourseBuilder}
                    onTabClick={setTabState}
                  >
                    Course Builder
                  </TabHeader>
                )}
                {tabsEnabled.menuBuilder && (
                  <TabHeader
                    name={ClassroomDetailsTabs.MenuBuilder}
                    onTabClick={setTabState}
                  >
                    Menu Builder
                  </TabHeader>
                )}
                <TextTooltip
                  text={
                    classroom?.isArchived &&
                    "Sim Pass is unavailable because this classroom is archived"
                  }
                >
                  <TabHeader
                    name={ClassroomDetailsTabs.SimPass}
                    onTabClick={setTabState}
                    disabled={classroom?.isArchived}
                  >
                    <FontAwesomeIcon icon={["fa-regular", "key-skeleton"]} />{" "}
                    <span>Sim Pass</span>
                  </TabHeader>
                </TextTooltip>
                {tabsEnabled.missionControl && (
                  <TextTooltip
                    text={
                      classroom?.isArchived &&
                      "Mission Control is unavailable because this classroom is archived"
                    }
                  >
                    <TabHeader
                      name={ClassroomDetailsTabs.MissionControl}
                      onTabClick={handleMissionControlTabClick}
                      disabled={classroom?.isArchived}
                    >
                      {"Mission Control"}
                      <span className="badge">{liveStudentCount}</span>
                    </TabHeader>
                  </TextTooltip>
                )}
                {tabsEnabled.gradebook && (
                  <TabHeader
                    name={ClassroomDetailsTabs.Gradebook}
                    onTabClick={setTabState}
                  >
                    Gradebook
                  </TabHeader>
                )}
              </TabHeaders>

              <ClassroomProgressTab
                name={ClassroomDetailsTabs.Progress}
                className="classroom-tabs-content"
                classroom={classroom}
                users={users}
                responsiveStyles={responsiveStyles}
                courseData={courseInfo}
                activityStatus={activityStatus}
                courseDataLoading={courseDataLoading}
              />
              <ClassroomUsersTab
                name={userTabTitle}
                className="classroom-tabs-content"
                responsiveStyles={responsiveStyles}
                users={users}
                getUsers={getUsers}
                classroom={classroom}
                readOnly={!isClassroomEditable}
                onUsersUpdate={onUsersUpdate}
                openRosterModal={setOpenRosterModal}
                rosterModalOpened={openRosterModal}
                statusFilter={statusFilter}
                onStatusFilterChange={setStatusFilter}
                loading={loadingUsers}
              />
              {!classroom?.isProfessionalLearning && (
                <Tab
                  name={ClassroomDetailsTabs.QR}
                  className="classroom-tabs-content"
                >
                  <ClassroomQRTab classroom={classroom} />
                </Tab>
              )}
              {tabsEnabled.menuBuilder && (
                <ClassroomMenuBuilderTab
                  name={ClassroomDetailsTabs.MenuBuilder}
                  className="classroom-tabs-content"
                  classroom={classroom}
                  readOnly={!isClassroomEditable}
                />
              )}
              <Tab
                name={ClassroomDetailsTabs.SimPass}
                className="classroom-tabs-content"
              >
                <ClassroomSimPassTab
                  responsiveStyles={responsiveStyles}
                  users={users}
                  classroom={classroom}
                  isTsClassroom={activeProduct === Product.TS}
                  isProfessionalLearning={classroom?.isProfessionalLearning}
                />
              </Tab>
              {tabsEnabled.missionControl && (
                <Tab
                  name={ClassroomDetailsTabs.MissionControl}
                  className="classroom-tabs-content"
                >
                  <ClassroomMissionControlTab
                    classroom={classroom}
                    users={users}
                  />
                </Tab>
              )}
              {tabsEnabled.gradebook && (
                <ClassroomGradebookTab
                  name={ClassroomDetailsTabs.Gradebook}
                  className="classroom-tabs-content"
                  classroom={classroom}
                  users={users}
                  selectedUser={selectedUser}
                  activityStatus={activityStatus}
                  product={activeProduct}
                  readOnly={!isClassroomEditable}
                />
              )}
              {tabsEnabled?.courseBuilder && (
                <ClassroomCourseBuilderTab
                  name={ClassroomDetailsTabs.CourseBuilder}
                  className="classroom-tabs-content"
                  courseData={courseInfo}
                  loading={courseDataLoading}
                  readOnly={
                    !isClassroomEditable ||
                    courseInfo?.sourceClassroomId != classroomId
                  }
                  classroomOrgCode={classroomOrgCode}
                  classroomId={classroomId}
                  onCourseBuilt={getCourseInfo}
                  onResetSuccess={getCourseInfo}
                />
              )}
            </Tabs>
          </div>
        </div>
        <RosterModal
          onClose={() => setOpenRosterModal(false)}
          show={openRosterModal}
          responsiveStyles={responsiveStyles}
          classroomId={classroomId}
          product={activeProduct}
        />
      </PageLayout>
    )
  );
};
